import { Subject } from "rxjs";
import moment from "moment";
import { Show, ShowMetaData } from "./show";
import { ExportConfiguration } from "./export-configuration";
import { DynamicPromoInfo } from "./dyanmicPromo";
import { Layout } from "./layouts/layout";

export enum EReplaceShowStrategy {
  Default,
  KeepDuration,
  CreateGap,
}
export class ExtendedSubjectVoid extends Subject<void> {
  override next(value: void) {
    //console.trace(`show-repository->call to next()`);
    super.next(value);
  }
}
export enum ECopyShowsResult {
  OK,
  SecondaryEventCommandsMissing,
  OverlapShowsForbidden,
  OverlapAnchoredShows,
}
export abstract class ShowRepository {
  onShowsChange: ExtendedSubjectVoid;

  init: () => void;

  getByDates: (
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    shouldIncludeSecEvents?: boolean,
    abortSubject?: Subject<void>,
    includeLiveFeedLatencies?: boolean,
    logResponse?: boolean,
  ) => Promise<Show[]>;

  closeGapAfter: (show: Show) => Promise<void>;
  closeGaps: (showIds: Array<string>) => Promise<any>;
  createMediaShow: (
    mediaId: number,
    startTime: moment.Moment,
    somMs?: number,
    durationMs?: number,
    showName?: string,
    isAnchor?: boolean,
    showMetadata?: ShowMetaData,
  ) => Promise<void>;
  createMediaShowForChannel: (
    channelId: number,
    mediaId: number,
    startTime: moment.Moment,
    somMs?: number,
    durationMs?: number,
    showName?: string,
    isAnchor?: boolean,
    showMetadata?: ShowMetaData,
  ) => Promise<void>;
  updateShowDisabledSecEvents: (
    showId: string,
    disabledSecEventIds: Array<string>,
    shouldUpdateShows: boolean,
  ) => Promise<void>;
  createLiveShow: (
    feedId: string,
    startTime: moment.Moment,
    duration: moment.Duration,
  ) => Promise<void>;
  createDynamicPromoShow: (
    dynamicPromoInfo: DynamicPromoInfo,
    startTime: moment.Moment,
  ) => Promise<void>;
  createLayoutShow: (
    layoutUid: string,
    startTime: moment.Moment,
    durationMs: number,
  ) => Promise<void>;
  createGraphicsShow: (
    graphicsId: number,
    graphicsUid: string,
    startTime: moment.Moment,
  ) => Promise<void>;
  createBroadcastPresetShow: (
    broadcastPresetUid: string,
    startTime: moment.Moment,
    durationMs: number,
  ) => Promise<void>;
  updateShowTime: (showId: string, date: moment.Moment) => Promise<void>;
  updateShowComment: (showId: string, comment: string) => Promise<void>;

  deleteShow: (showId: string) => Promise<void>;
  updatePrerollMedia: (
    channelId: number,
    prerollShowIds: string[],
    currentPlayingShowId: string,
    clientId: string,
  ) => Promise<void>;

  clearChannelPrerollList: (
    channelId: number
  ) => Promise<void>;

  deleteShows: (showIds: string[]) => Promise<void>;
  deleteChannelShows: (channelId: number, showIds: string[]) => Promise<void>;

  exportEpg: (
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    minDuration: number,
    channelName: string,
    exportConfigId: number,
    exportFormat: string,
  ) => Promise<void>;

  exportRunLog: (
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    channelName: string,
    exportConfigId: number,
    exportAsMarinaXml: boolean,
  ) => Promise<void>;

  getExportConfigurations: () => Promise<ExportConfiguration[]>;

  updateExportConfiguration: (
    exportConfiguration: ExportConfiguration,
  ) => Promise<void>;

  updateLiveBroadcastStatus: (
    channelId: number,
    isActive: boolean,
  ) => Promise<void>;

  addExportConfiguration: (
    exportConfiguration: ExportConfiguration,
  ) => Promise<any>;

  updatePlayout: (channelId) => Promise<void>;

  replaceShow: (
    showId: string,
    customMediaId: string,
    applyToAllShowsWithSameMedia: boolean,
    replaceStrategy: EReplaceShowStrategy,
    replaceMediaUntil?: moment.Moment,
  ) => Promise<void>;

  renameShow: (
    showId: string,
    newName: string,
    raiseChannelScheduleUpdatedEvent?: boolean,
  ) => Promise<void>;

  updateShowDuration: (
    showId: string,
    duration: moment.Moment | number,
    raiseChannelScheduleUpdatedEvent?: boolean,
  ) => Promise<void>;

  updateShowSom: (
    showId: string,
    duration: number,
    raiseChannelScheduleUpdatedEvent?: boolean,
  ) => Promise<void>;

  copyShowsWithSecondaryEvents: (
    targetChannelId: number,
    showFrom: Show,
    showTo: Show,
    startTime: moment.Moment,
    forceCreatingSecondaryEventCommandSettings: boolean,
    checkSecondaryEventsCommandSettings?: boolean,
    copyContracts?: boolean,
    repeatUntil?: moment.Moment,
    copyPartial?: boolean,
  ) => Promise<ECopyShowsResult>;

  arrangeShows: (
    targetChannelId: number,
    startTime: moment.Moment,
    endTime: moment.Moment,
    fireEvent: boolean,
  ) => Promise<void>;

  getLiveBroadcastingState: (channelId: number) => Promise<Show[]>;
  getNextShow: (
    channelId: number,
    startDate: moment.Moment,
  ) => Promise<Show | Error>;
  updateShowAnchorState: (showId: string, state: boolean) => void;
}
